// Alpine `anchorHighlight`: deep-link landing for mention notifications.
//
// NotificationsController redirects to e.g. /customers/1?highlight=activity-<id>#activity-<id>.
// Turbo follows that PATCH redirect with fetch, which drops the fragment, so
// the anchor is read from location.hash or ?highlight=, the URL is cleaned to
// a plain #anchor, and the target is scrolled into view, focused and ringed
// for a few seconds. Mounted once in the application layout.
//
// Only mention anchors ("activity-<uuid>", "message_<uuid>",
// "lead-comment-<uuid>", "renewal-<uuid>") are handled: a Mentionable's
// mention_anchor must keep this type-uuid shape.
// Every other fragment (the layout's skip links, in-page links) is left to the
// browser's native jump.
//
// The highlight is one-shot per arrival: the history entry is marked once
// handled, so a Turbo restoration visit (Back) keeps Turbo's restored scroll
// position instead of jumping to the note again.
const MENTION_ANCHOR = /^([a-z]+(?:-[a-z]+)*)[-_]([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const RING = ["ring-2", "ring-primary-500", "ring-offset-2", "dark:ring-offset-gray-900"];
const HIGHLIGHT_MS = 3000;
const STATE_KEY = "mentionHighlighted";

// When the anchored item isn't on the page (the timeline shows the latest 20
// activities), open it another way.
const FALLBACKS = {
  activity: (id) => {
    const frame = document.getElementById("activity-detail-modal");
    if (frame) frame.src = `/activities/${id}/modal`;
  }
};

export function isMentionAnchor(anchor) {
  return typeof anchor === "string" && MENTION_ANCHOR.test(anchor);
}

function decode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return "";
  }
}

export function readAnchor(location, history) {
  const url = new URL(location.href);
  const fromHash = decode((url.hash || "").replace(/^#/, ""));
  const fromParam = url.searchParams.get("highlight") || "";
  const anchor = isMentionAnchor(fromHash) ? fromHash : isMentionAnchor(fromParam) ? fromParam : null;

  if (url.searchParams.has("highlight")) {
    url.searchParams.delete("highlight");
    if (anchor) url.hash = anchor;
    history?.replaceState(history.state, "", url.toString());
  }
  return anchor;
}

// True when this history entry already highlighted `anchor`.
export function alreadyHighlighted(history, anchor) {
  return !!anchor && history?.state?.[STATE_KEY] === anchor;
}

// Marks the current history entry, keeping Turbo's own state keys.
export function markHighlighted(history, anchor, href) {
  if (!history?.replaceState) return;
  const state = history.state && typeof history.state === "object" ? history.state : {};
  history.replaceState({ ...state, [STATE_KEY]: anchor }, "", href);
}

export function anchorHighlightData() {
  return {
    init() {
      this._run = () => {
        const anchor = readAnchor(window.location, window.history);
        if (!anchor || alreadyHighlighted(window.history, anchor)) return;
        markHighlighted(window.history, anchor, window.location.href);
        this.highlight(anchor);
      };
      this.$nextTick(() => requestAnimationFrame(this._run));
      window.addEventListener("hashchange", this._run);
    },

    destroy() {
      window.removeEventListener("hashchange", this._run);
    },

    find(anchor) {
      return (
        document.getElementById(anchor) ||
        document.querySelector(`[data-mention-anchor="${window.CSS?.escape ? CSS.escape(anchor) : anchor}"]`)
      );
    },

    highlight(anchor) {
      if (!isMentionAnchor(anchor)) return;
      const el = this.find(anchor);
      if (!el) {
        const [, type, id] = anchor.match(MENTION_ANCHOR);
        const fallback = FALLBACKS[type.toLowerCase()];
        if (fallback) fallback(id.toLowerCase());
        return;
      }

      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      el.focus({ preventScroll: true });
      el.classList.add(...RING);
      el.dataset.highlighted = "true";
      clearTimeout(el._mentionHighlightTimer);
      el._mentionHighlightTimer = setTimeout(() => {
        el.classList.remove(...RING);
        delete el.dataset.highlighted;
      }, HIGHLIGHT_MS);
    }
  };
}

export default function registerAnchorHighlight(Alpine) {
  Alpine.data("anchorHighlight", anchorHighlightData);
}
